import {noop} from '@benchling/js-utils';
import React, {useState} from 'react';

import {Owner} from 'auth/PermissionConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  TEST_DEFINITION_UPDATE_STARTED_EVENT,
  TEST_DEFINITION_UPDATE_ENDED_EVENT,
} from 'performance/processSampleTestsEvents';
import * as TestDefinitionDAO from 'sample-tests/network/TestDefinitionDAO';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import {withLoadingScreen} from 'util/Promises';

import {TestDefinitionQuery} from './__generated/TestDefinitionQuery';
import TestDefinitionMetadataSettings from './TestDefinitionMetadataSettings';
import TestDefinitionPermissionSettings from './TestDefinitionPermissionSettings';

type MaybeInaccessibleTestDefinition = TestDefinitionQuery['testDefinition'];

type TestDefinition = ExtractByTypename<MaybeInaccessibleTestDefinition, 'TestDefinitionNode'>;

enum Tabs {
  TEST_DEFINITION_EDITOR = 'TEST_DEFINITION_EDITOR',
  TEST_DEFINITION_ACCESS_POLICIES = 'TEST_DEFINITION_ACCESS_POLICIES',
}

export interface ExistingTestDefinitionEditorProps {
  testDefinition: TestDefinition;
  ownerChoices: Array<Owner>;
  refetch: () => void;
}
export default function ExistingTestDefinitionEditor(props: ExistingTestDefinitionEditorProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.TEST_DEFINITION_EDITOR);
  const updateTestDefinition = async ({
    name,
    description,
    resultSchemaId,
  }: {
    name?: string;
    description?: string;
    resultSchemaId?: string;
  }): Promise<void> => {
    logProfilerTimingEvent(TEST_DEFINITION_UPDATE_STARTED_EVENT);
    await withLoadingScreen(
      (async () => {
        const response = await TestDefinitionDAO.updateTestDefinition({
          id: props.testDefinition.id,
          name: name ? name.trim() : props.testDefinition.name,
          description: description ?? props.testDefinition.description,
          organizationId: props.testDefinition.organization.id,
          // ResultSchemaSelector UI should prevent selecting inaccessible result schema
          // On the off chance that an inaccessible schema makes its way thru, rely on backend validation
          resultSchemaId:
            resultSchemaId ?? getAccessibleOrInaccessibleId(props.testDefinition.resultSchemaCorrect),
        });
        if (response == null) {
          return;
        }
        notifications.success('Updated test definition successfully');
        props.refetch();
      })(),
      'Updating test definition...'
    );

    logProfilerTimingEvent(TEST_DEFINITION_UPDATE_ENDED_EVENT);
  };

  const setName = (name: string): void => {
    updateTestDefinition({name});
  };

  const setDescription = (description: string): void => {
    updateTestDefinition({description});
  };

  const setResultSchemaId = (resultSchemaId: string): void => {
    updateTestDefinition({resultSchemaId});
  };

  return (
    <TabContainer
      onRequestTabChange={(tab: Tabs) => {
        setActiveTab(tab);
      }}
      activeTab={activeTab}
      className="u-flex-grow"
    >
      <Tab tabKey={Tabs.TEST_DEFINITION_EDITOR} title="Test definition settings">
        <TestDefinitionMetadataSettings
          ownerId={props.testDefinition.organization.id}
          setOwnerId={noop /* Owner can only be set on creation */}
          name={props.testDefinition.name}
          setName={setName}
          description={props.testDefinition.description}
          setDescription={setDescription}
          resultSchemaId={getAccessibleOrInaccessibleId(props.testDefinition.resultSchemaCorrect)}
          setResultSchemaId={setResultSchemaId}
          ownerChoices={props.ownerChoices}
          testDefinition={props.testDefinition}
          handleNext={() => {
            setActiveTab(Tabs.TEST_DEFINITION_ACCESS_POLICIES);
          }}
        />
      </Tab>
      <Tab tabKey={Tabs.TEST_DEFINITION_ACCESS_POLICIES} title="Access Policies">
        <TestDefinitionPermissionSettings testDefinitionId={props.testDefinition.id} />
      </Tab>
    </TabContainer>
  );
}
